import {PageHeader} from "../menu/PageHeader";
import {Badge, Button, Card, Col, Container, Row} from "react-bootstrap";
import React, {useContext, useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {UsacmContext} from "../App";
import {getCourseCart, getCourseCatalog} from "../api/CourseApi";
import {getErrorMessageForField} from "../utils/formUtils";
import {CONF_LABEL_KEY_COURSE, CONF_LABEL_KEY_COURSE_PAGE} from "../constants";
import {getConfLabel, makePlural, titleCase} from "../utils/usacmUtils";
import {CoursePriceList} from "./CoursePriceList";
import "./CourseList.scss";

/**
 * The course catalog - one full width card per published course which has a price that can be bought today.
 * Each price has its own button (see CoursePriceList), and clicking elsewhere on a card opens the course's own page.
 */
export function CourseList() {
  const context = useContext(UsacmContext);
  const [conf,] = context.conference;
  const [navChanged,] = context.navigationChanged;
  const navigate = useNavigate();
  const courseLabel = titleCase(getConfLabel(conf, CONF_LABEL_KEY_COURSE));
  const pageLabel = titleCase(getConfLabel(conf, CONF_LABEL_KEY_COURSE_PAGE));
  const [courseList, setCourseList] = useState(null);
  const [cartCount, setCartCount] = context.courseCartCount;
  const [errors, setErrors] = useState([]);

  function loadCatalog() {
    getCourseCatalog((status, data, newErrors) => {
      if (status === 200) {
        setCourseList(data);
        setErrors([]);
      } else {
        setErrors(newErrors);
      }
    });
    getCourseCart((status, data) => {
      if (status === 200) {
        setCartCount(data.items.length);
      }
    });
  }

  useEffect(() => {
    loadCatalog();
  }, [navChanged]);

  // Clicking anywhere on a card opens the course's own page - except on its buttons and links
  function onCardClick(event, course) {
    if (event.target.closest('a, button')) {
      return;
    }
    navigate('/course?id=' + course.id);
  }

  return (
    <div>
      <PageHeader pageTitle={makePlural(courseLabel)}/>
      <Container fluid className="usacm-container-wide">

        <div className="d-flex justify-content-end mb-3">
          <Button type="button" variant="outline-primary" onClick={() => navigate('/course-cart')}>
            <FontAwesomeIcon icon="fa-cart-shopping" className="me-2"/>Cart ({cartCount})
          </Button>
        </div>

        {getErrorMessageForField(errors, '') &&
          <Row>
            <Col className="text-center mb-3 usacm-error-message">
              {getErrorMessageForField(errors, '')}
            </Col>
          </Row>
        }

        {courseList && courseList.length === 0 &&
          <Row>
            <Col className="text-center">
              There are no {makePlural(courseLabel).toLowerCase()} available right now.
            </Col>
          </Row>
        }

        {(courseList || []).map(course =>
          <Card key={course.id} className="mb-4 course-catalog-card course-catalog-card-link"
                onClick={(event) => onCardClick(event, course)}>
            <Row className="g-0">
              <Col md={4} lg={3}>
                {course.image_url ?
                  <img src={course.image_url} alt="" className="course-catalog-image"/>
                  :
                  <div className="course-catalog-image course-catalog-image-placeholder">
                    <FontAwesomeIcon icon="fa-graduation-cap" size="3x"/>
                  </div>
                }
              </Col>
              <Col md={8} lg={9}>
                <Card.Body>
                  <Card.Title>
                    {course.title}
                    {course.has_access && <Badge bg="success" className="ms-2 align-middle">Enrolled</Badge>}
                    {!course.has_access && course.pending_review &&
                      <Badge bg="warning" text="dark" className="ms-2 align-middle">Pending Review</Badge>
                    }
                  </Card.Title>
                  {course.subtitle && <Card.Subtitle className="mb-2 text-muted">{course.subtitle}</Card.Subtitle>}
                  {course.description &&
                    <div className="course-catalog-description"
                         dangerouslySetInnerHTML={{__html: course.description}}/>
                  }
                  <div className="small text-muted mb-2">
                    {course.page_count} {course.page_count === 1 ? pageLabel : makePlural(pageLabel)}
                  </div>

                  <CoursePriceList course={course} onChanged={loadCatalog} setErrors={setErrors}/>

                  {course.has_access &&
                    <div className="text-end mt-3">
                      <Button type="button" onClick={() => navigate('/my-course-list?courseid=' + course.id)}>
                        View {courseLabel}
                      </Button>
                    </div>
                  }
                </Card.Body>
              </Col>
            </Row>
          </Card>
        )}

      </Container>
    </div>
  );
}
